import React from "react";
import { Guid } from "guid-typescript";
import { createPortal } from "react-dom";

import "./ColourPicker.scss";
import Utility from "../../foundation/Utility";

interface ColourPickerProps {
    id?: string;
    label?: string;
    value?: string;
    width?: number | string;
    minTime?: Date;
    maxTime?: Date;
    disabled?: boolean;
    className?: string;
    tooltip?: string;
    tooltipPlace?: string;
    onChange?: (value: string) => void;
}

interface ColourPickerState {
    ref: React.RefObject<HTMLLabelElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    colourRef: React.RefObject<HTMLCanvasElement>;
    hueRef: React.RefObject<HTMLCanvasElement>;
    opacityRef: React.RefObject<HTMLCanvasElement>;
    anchor: string;
    colourTop: number;
    colourLeft: number;
    hueTop: number;
    opacityTop: number;
    open: boolean;
    focused: boolean;
    current: string;
    currentWorking: number[];
}

class ColourPicker extends React.Component<
    ColourPickerProps,
    ColourPickerState
> {
    state: ColourPickerState = {
        ref: React.createRef<HTMLLabelElement>(),
        inputRef: React.createRef<HTMLInputElement>(),
        colourRef: React.createRef<HTMLCanvasElement>(),
        hueRef: React.createRef<HTMLCanvasElement>(),
        opacityRef: React.createRef<HTMLCanvasElement>(),
        anchor: `--${Guid.create().toString()}`,
        colourTop: 0,
        colourLeft: 0,
        hueTop: 0,
        opacityTop: 0,
        open: false,
        focused: false,
        current: "",
        currentWorking: [],
    };

    componentDidMount(): void {
        if (this.props.value) {
            const value = this.props.value || "";
            this.setState({
                current: value,
                currentWorking: value ? this.hexToHsl(value) : [0, 0, 100, 100],
            });
        }
    }

    componentDidUpdate(
        prevProps: Readonly<ColourPickerProps>,
        prevState: Readonly<ColourPickerState>,
        snapshot?: any,
    ): void {
        if (prevProps.value !== this.props.value) {
            const value = this.props.value || "";
            this.setState({
                current: value,
                currentWorking: value ? this.hexToHsl(value) : [0, 0, 100, 100],
            });

            if (this.state.open) this.open();
        }
        if (prevState.current !== this.state.current) {
            const value = this.state.current || "";
            this.setState({
                current: value,
                currentWorking: value ? this.hexToHsl(value) : [0, 0, 100, 100],
            });
        }
    }

    rgbToHsla(r: number, g: number, b: number, a: number) {
        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0,
            s = 0,
            l = (max + min) / 2;
        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }
        return [
            Math.round(h * 360),
            Math.round(s * 100),
            Math.round(l * 100),
            Math.round(a * 100),
        ];
    }

    hslToRgb(h: number, s: number, l: number, a: number) {
        s /= 100;
        l /= 100;
        const k = n => (n + h / 30) % 12;
        const a_ = s * Math.min(l, 1 - l);
        const f = n =>
            l - a_ * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
        return [
            Math.round(255 * f(0)),
            Math.round(255 * f(8)),
            Math.round(255 * f(4)),
            a / 100,
        ];
    }

    hexToRgba(hex: string) {
        hex =
            hex.length === 4 ? `${hex}f` : hex.length === 7 ? `${hex}ff` : hex;
        hex =
            hex.length === 5
                ? `${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}${hex[4]}${hex[4]}`
                : hex;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const a = parseInt(hex.slice(7, 9), 16) / 255;
        return [r, g, b, a];
    }

    hexToHsl(hex: string) {
        const rgba = this.hexToRgba(hex);
        return this.rgbToHsla(rgba[0], rgba[1], rgba[2], rgba[3]);
    }

    rgbaToHex(r: number, g: number, b: number, a: number) {
        const toHex = n => n.toString(16).padStart(2, "0");
        const alphaHex = toHex(Math.round(a * 255));
        return `#${toHex(r)}${toHex(g)}${toHex(b)}${
            alphaHex === "ff" ? "" : alphaHex
        }`;
    }

    hslToHex(h: number, s: number, l: number, a: number) {
        const rgba = this.hslToRgb(h, s, l, a);
        return this.rgbaToHex(rgba[0], rgba[1], rgba[2], rgba[3]);
    }

    canvasRender() {
        const hsl = this.state.currentWorking;
        this.renderColourCanvas(hsl);
        this.renderHueCanvas();
        this.renderOpacityCanvas(hsl);

        this.setState({
            hueTop: (hsl[0] / 360) * 255 - 6,
            opacityTop: 255 - (hsl[3] / 100) * 255 - 6,
            colourTop: 255 - (hsl[2] / 100) * 255 - 10,
            colourLeft: (hsl[1] / 100) * 255 - 10,
        });
    }

    renderColourCanvas(hsl: number[]) {
        const graphics = this.state.colourRef.current?.getContext("2d");
        if (graphics) {
            const width = 255;
            const height = 255;

            const gradientH = graphics.createLinearGradient(0, 0, width, 0);
            gradientH.addColorStop(0, "#fff");
            gradientH.addColorStop(1, this.hslToHex(hsl[0], 100, 50, 100));
            graphics.fillStyle = gradientH;
            graphics.fillRect(0, 0, width, height);

            const gradientV = graphics.createLinearGradient(0, 0, 0, height);
            gradientV.addColorStop(0, "#00000000");
            gradientV.addColorStop(1, "#000");
            graphics.fillStyle = gradientV;
            graphics.fillRect(0, 0, width, height);
        }
    }

    renderHueCanvas() {
        const graphics = this.state.hueRef.current?.getContext("2d");
        if (graphics) {
            const width = 30;
            const height = 255;

            const gradient = graphics.createLinearGradient(0, 0, 0, height);
            for (let i = 0; i <= 360; i += 1) {
                gradient.addColorStop(i / 360, this.hslToHex(i, 100, 50, 100));
            }
            graphics.fillStyle = gradient;
            graphics.fillRect(0, 0, width, height);
        }
    }

    renderOpacityCanvas(hsl: number[]) {
        const graphics = this.state.opacityRef.current?.getContext("2d");
        if (graphics) {
            let alt = false;
            const size = 5;
            const width = 30;
            const height = 255;

            for (let x = 0; x < width; x += size) {
                for (let y = 0; y < height; y += size) {
                    graphics.fillStyle = alt ? "#fff" : "#ccc";
                    graphics.fillRect(x, y, size, size);
                    alt = !alt;
                }
            }

            const gradient = graphics.createLinearGradient(0, 0, 0, height);
            gradient.addColorStop(0, this.hslToHex(hsl[0], 100, 50, 100));
            gradient.addColorStop(1, "#00000000");
            graphics.fillStyle = gradient;
            graphics.globalAlpha = 1;
            graphics.fillRect(0, 0, width, height);
        }
    }

    open() {
        this.setState(
            {
                open: true,
            },
            this.canvasRender.bind(this),
        );
    }

    close() {
        this.setState({ open: false });
    }

    toggle() {
        if (this.props.disabled) return;
        if (this.state.open) this.close();
        else this.open();
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.close();
            }
        }, 200);
    }

    handleClick(_event: React.MouseEvent<HTMLInputElement>) {
        if (this.props.disabled) return;
        this.open();
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
                this.toggle();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Escape":
                this.close();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    inputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        this.setState({ current: value });
        if (this.props.onChange) this.props.onChange(value);
    }

    handleColourClick(event: React.MouseEvent<HTMLCanvasElement>) {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const hsl = this.state.currentWorking;
        const saturation = Math.round(
            Math.max(0, Math.min(100, (x / 255) * 100)),
        );
        const lightness = Math.round(
            Math.max(0, Math.min(100, 100 - (y / 255) * 100)),
        );

        const hex = this.hslToHex(hsl[0], saturation, lightness, hsl[3]);
        this.setState({
            current: hex,
            colourLeft: Math.min(Math.max(x - 10, -10), 245),
            colourTop: Math.min(Math.max(y - 10, -10), 245),
        });

        if (this.props.onChange) this.props.onChange(hex);
    }
    handleColourMouseDown(event: React.MouseEvent<HTMLCanvasElement>) {
        const target = event.currentTarget;
        const moveListener = Utility.throttle((moveEvent: MouseEvent) => {
            this.handleColourClick({
                currentTarget: target,
                clientX: moveEvent.clientX,
                clientY: moveEvent.clientY,
            } as React.MouseEvent<HTMLCanvasElement>);
        }, 50);
        const upListener = (_upEvent: MouseEvent) => {
            window.removeEventListener("mousemove", moveListener);
            window.removeEventListener("mouseup", upListener);
        };
        window.addEventListener("mousemove", moveListener);
        window.addEventListener("mouseup", upListener);
    }

    handleHueClick(event: React.MouseEvent<HTMLCanvasElement>) {
        const rect = event.currentTarget.getBoundingClientRect();
        const y = event.clientY - rect.top;

        const hsl = this.state.currentWorking;
        const hue = Math.round(Math.max(0, Math.min(360, (y / 255) * 360)));

        const hex = this.hslToHex(hue, hsl[1], hsl[2], hsl[3]);
        this.setState({
            current: hex,
            hueTop: Math.min(Math.max(y - 6, -6), 249),
        });

        if (this.props.onChange) this.props.onChange(hex);
    }
    handleHueMouseDown(event: React.MouseEvent<HTMLCanvasElement>) {
        const target = event.currentTarget;
        const moveListener = Utility.throttle((moveEvent: MouseEvent) => {
            this.handleHueClick({
                currentTarget: target,
                clientX: moveEvent.clientX,
                clientY: moveEvent.clientY,
            } as React.MouseEvent<HTMLCanvasElement>);
        }, 50);
        const upListener = (_upEvent: MouseEvent) => {
            window.removeEventListener("mousemove", moveListener);
            window.removeEventListener("mouseup", upListener);
        };
        window.addEventListener("mousemove", moveListener);
        window.addEventListener("mouseup", upListener);
    }

    handleOpacityClick(event: React.MouseEvent<HTMLCanvasElement>) {
        const rect = event.currentTarget.getBoundingClientRect();
        const y = event.clientY - rect.top;

        const hsl = this.state.currentWorking;
        const opacity = Math.round(
            Math.max(0, Math.min(100, 100 - (y / 255) * 100)),
        );

        const hex = this.hslToHex(hsl[0], hsl[1], hsl[2], opacity);
        this.setState({
            current: hex,
            opacityTop: Math.min(Math.max(y - 6, -6), 249),
        });

        if (this.props.onChange) this.props.onChange(hex);
    }
    handleOpacityMouseDown(event: React.MouseEvent<HTMLCanvasElement>) {
        const target = event.currentTarget;
        const moveListener = Utility.throttle((moveEvent: MouseEvent) => {
            this.handleOpacityClick({
                currentTarget: target,
                clientX: moveEvent.clientX,
                clientY: moveEvent.clientY,
            } as React.MouseEvent<HTMLCanvasElement>);
        }, 50);
        const upListener = (_upEvent: MouseEvent) => {
            window.removeEventListener("mousemove", moveListener);
            window.removeEventListener("mouseup", upListener);
        };
        window.addEventListener("mousemove", moveListener);
        window.addEventListener("mouseup", upListener);
    }

    render(): React.ReactNode {
        const rect = this.state.ref.current?.getBoundingClientRect();
        const isTop = (rect ? rect.top / window.innerHeight : 0) > 0.5;

        const maxHeight = Math.min(
            300,
            isTop
                ? rect?.top - 5
                : window.innerHeight - (rect?.bottom ?? 0) - 3,
        );

        return (
            <label
                ref={this.state.ref}
                className={`colour-picker ${this.props.className}`}
                style={{
                    width: this.props.width,
                    anchorName: this.state.anchor,
                }}
                data-html={true}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace}>
                <input
                    type="text"
                    ref={this.state.inputRef}
                    value={this.state.current}
                    onFocus={this.handleFocus.bind(this)}
                    onBlur={this.handleBlur.bind(this)}
                    onClick={this.handleClick.bind(this)}
                    onKeyDown={this.handleKeyDown.bind(this)}
                    onKeyUp={this.handleKeyUp.bind(this)}
                    onChange={this.inputChange.bind(this)}
                />
                <div
                    className="colour-preview"
                    style={{ background: this.state.current }}
                />
                {this.props.label && (
                    <span className="text">{this.props.label}</span>
                )}
                {this.state.open &&
                    createPortal(
                        <div
                            tabIndex={0}
                            className={`colour-display${isTop ? " top" : ""}`}
                            style={{
                                positionAnchor: this.state.anchor,
                                maxHeight: `${maxHeight}px`,
                            }}
                            onFocus={this.handleFocus.bind(this)}
                            onBlur={this.handleBlur.bind(this)}>
                            <div className="colour-selection">
                                <div className="colour-gradient">
                                    <canvas
                                        ref={this.state.colourRef}
                                        width={255}
                                        height={255}
                                        onClick={this.handleColourClick.bind(
                                            this,
                                        )}
                                        onMouseDown={this.handleColourMouseDown.bind(
                                            this,
                                        )}
                                    />
                                    <div
                                        className="indicator"
                                        style={{
                                            top: `${this.state.colourTop}px`,
                                            left: `${this.state.colourLeft}px`,
                                        }}
                                    />
                                </div>
                                <div className="colour-hue">
                                    <canvas
                                        ref={this.state.hueRef}
                                        width={30}
                                        height={255}
                                        onClick={this.handleHueClick.bind(this)}
                                        onMouseDown={this.handleHueMouseDown.bind(
                                            this,
                                        )}
                                    />
                                    <div
                                        className="indicator"
                                        style={{
                                            top: `${this.state.hueTop}px`,
                                        }}
                                    />
                                </div>
                                <div className="colour-opacity">
                                    <canvas
                                        ref={this.state.opacityRef}
                                        width={30}
                                        height={255}
                                        onClick={this.handleOpacityClick.bind(
                                            this,
                                        )}
                                        onMouseDown={this.handleOpacityMouseDown.bind(
                                            this,
                                        )}
                                    />
                                    <div
                                        className="indicator"
                                        style={{
                                            top: `${this.state.opacityTop}px`,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>,
                        document.querySelector(".page-elements"),
                    )}
            </label>
        );
    }
}

export default ColourPicker;
